// Mock data for Teach Your Child Credit - content adapted from the ebook by Ken Bossard, MBA
// All progress is stored in the browser (localStorage) for now.

export const BOOK_URL = "https://www.amazon.com/dp/B0D6SRQJ3P";

export const PANDA_ACCESSORIES = [
  { id: "none", label: "Just me" },
  { id: "glasses", label: "Glasses" },
  { id: "bowtie", label: "Bow tie" },
  { id: "hat", label: "Party hat" },
  { id: "crown", label: "Crown" },
  { id: "cap", label: "Grad cap" },
];

export const PANDA_SCARVES = [
  { id: "#e5356f", label: "Pink" },
  { id: "#1b8bc9", label: "Blue" },
  { id: "#63c79a", label: "Green" },
  { id: "#f7931e", label: "Orange" },
  { id: "#7d5fb2", label: "Purple" },
  { id: "#f3c932", label: "Yellow" },
];

export const STEPS = [
  { id: "learn", label: "Learn", icon: "BookOpen" },
  { id: "talk", label: "Talk", icon: "MessageCircle" },
  { id: "practice", label: "Practice", icon: "PencilLine" },
  { id: "story", label: "Story", icon: "Sparkles" },
  { id: "dodont", label: "Do & Don't", icon: "ThumbsUp" },
  { id: "quiz", label: "Quiz", icon: "Trophy" },
];

export const modules = [
  {
    id: "m1",
    slug: "promise-jars",
    num: 1,
    ages: "5-8",
    short: "Promise Jars",
    title: "Money Basics & the Idea of a Promise",
    tagline: "Sort your coins, keep your word",
    color: "#f3c932",
    dark: "#8a6d00",
    button: "#2e8b3d",
    pandaAccessory: "none",
    pandaScarf: "#e5356f",
    pandaItem: "coin",
    objectives: [
      "Recognize that money is earned, saved, or given, and each works differently.",
      "Understand a promise as something you keep, in words a young child can act on.",
      "Practice waiting to buy something instead of buying it immediately.",
    ],
    concepts: [
      {
        icon: "Handshake",
        title: "Credit starts as a promise",
        text: "Credit begins with the idea of a kept promise long before it becomes a number. The goal isn't vocabulary like 'FICO' or 'utilization' - it's the habit: if you say you'll do something, you do it, and people notice when you don't.",
      },
      {
        icon: "Archive",
        title: "Three jars: Spend, Save, Give",
        text: "Use three labeled jars or envelopes. Let your child sort every dollar they receive into the three. This makes the abstract idea of 'budgeting' physical and repeatable.",
      },
      {
        icon: "ClipboardList",
        title: "Tiny loans, big lesson",
        text: "Introduce small, low-stakes borrowing inside the family: 'I'll lend you the last two dollars for the toy, and you'll pay me back from your next allowance.' Track it on paper together so the child sees the debt exist and then disappear when repaid.",
      },
    ],
    script: [
      { who: "child", text: "Can I have this toy? It's four dollars and I only have two." },
      { who: "parent", text: "I can lend you the other two. Next time you get your allowance, the first two dollars go to paying me back - before Spend or Save. Deal?" },
      { who: "child", text: "Deal." },
      { who: "parent", text: "Let's write it on the fridge chart so we both remember." },
    ],
    worksheet: {
      title: "My Weekly Promise Chart",
      intro: "Fill this in together each week. Did the money go where you said it would?",
      columns: ["Week", "Money In", "Spend", "Save", "Give", "Promises Kept?"],
      rows: [["1"], ["2"], ["3"], ["4"]],
    },
    caseStudy: {
      title: "The Fridge Loan",
      text: "Maya, age 7, wanted a $6 book but had only $3 saved. Her father lent her the remaining $3 and wrote it on a family whiteboard: 'Maya owes Dad $3.' Each week, when Maya got her $2 allowance, $1 went to paying back the loan and $1 went to her Save jar. It took three weeks. When the loan was erased from the whiteboard, her father made a point of saying, 'You said you'd pay it back, and you did - that's exactly what a good credit history is.'",
      lesson: "Maya didn't need to understand credit scores to understand the feeling of a promise kept.",
    },
    dos: [
      "Make repayment visible (a chart, a jar, a whiteboard).",
      "Celebrate when a debt is paid off, not just when something is bought.",
    ],
    donts: [
      "Lend amounts the child can't realistically repay in a few weeks.",
      "Frame borrowing as free money - always name what has to come back.",
    ],
    quiz: [
      { q: "What are the three jars called?", options: ["Spend, Save, Give", "Toys, Candy, Games", "Big, Medium, Small", "Mine, Yours, Ours"], answer: 0, explain: "Every dollar gets sorted into Spend, Save or Give." },
      { q: "You borrowed $2 from a parent. What happens with your next allowance?", options: ["Buy another toy first", "The first $2 pays back the loan", "Put it all in Save", "Nothing - it was a gift"], answer: 1, explain: "Paying back first is how you keep the promise." },
      { q: "At your age, credit is really about...", options: ["Big numbers", "A promise you keep", "Getting free money", "Bank buildings"], answer: 1, explain: "Credit begins as a kept promise long before it becomes a number." },
      { q: "How did Maya's family remember her loan?", options: ["They forgot", "On the whiteboard", "In a secret code", "They didn't need to"], answer: 1, explain: "Making repayment visible helps everyone remember." },
      { q: "Why celebrate when a loan is paid off?", options: ["Because you can borrow more", "Because keeping a promise is what good credit is", "Because it's a holiday", "You shouldn't"], answer: 1, explain: "Celebrate the repayment, not just the purchase." },
    ],
  },
  {
    id: "m2",
    slug: "saving-and-borrowing",
    num: 2,
    ages: "9-11",
    short: "Interest Island",
    title: "Saving, Earning, and Borrowing Practice",
    tagline: "Watch money grow and learn what borrowing costs",
    color: "#f7931e",
    dark: "#8a4b00",
    button: "#1b6fb5",
    pandaAccessory: "bowtie",
    pandaScarf: "#1b8bc9",
    pandaItem: "bamboo",
    objectives: [
      "Set and reach a savings goal that takes more than a month.",
      "Understand interest as a fee for borrowing and a reward for saving.",
      "Practice a simple household 'loan' with an agreed repayment schedule and a very small interest charge.",
    ],
    concepts: [
      { icon: "TrendingUp", title: "Interest goes both ways", text: "A bank pays you a little for keeping your money there, and charges you more for lending you money. Open a real savings account together and let your child watch the balance grow, however slowly." },
      { icon: "FileText", title: "The household credit report", text: "Keep a single page that tracks every time the child borrowed from a parent and whether it was repaid on time. This mirrors, at a small scale, exactly what the three credit bureaus track for adults." },
      { icon: "MessageSquareText", title: "Use the real words", text: "Start saying 'payment history,' 'balance,' and 'limit.' A child who hears these words casually at nine will not find them intimidating at nineteen." },
    ],
    script: [
      { who: "parent", text: "You want the $30 bike helmet now, but you've only saved $18. I'll lend you $12, and I'll charge you $1 for the loan - that's interest. You'll owe me $13 total." },
      { who: "child", text: "Why do I have to pay extra?" },
      { who: "parent", text: "Because I'm giving up the use of that $12 while you have it. That's exactly what a bank charges you for a car loan, just much bigger." },
      { who: "child", text: "Okay. Can I pay it off faster if I want?" },
      { who: "parent", text: "Yes - and you won't owe any more interest than the $1 once it's paid, since we agreed on a flat fee, not a growing one." },
    ],
    worksheet: {
      title: "Household Credit Log",
      intro: "Log every family loan the same way every time.",
      columns: ["Date", "Amount Borrowed", "Interest Agreed", "Due Date", "Paid On Time? (Y/N)"],
      rows: [[], [], [], [], []],
    },
    caseStudy: {
      title: "The Household Credit Report",
      text: "The Alvarez family kept a single sheet on the refrigerator titled 'Household Credit Report' for their 10-year-old, Diego. Every loan - even $2 for extra screen time paid back in chores - was logged with a due date and a checkmark when repaid on time. After six months, Diego had a visible track record: nine loans, nine repaid on time. His parents used that record to justify raising his allowance and giving him a debit card linked to a parent-monitored account.",
      lesson: "A track record earns access to more.",
    },
    dos: ["Let the child see a real savings account grow.", "Log every household loan the same way every time."],
    donts: ["Make interest charges so large they feel punitive rather than instructive.", "Forgive a missed repayment without discussing why it happened."],
    quiz: [
      { q: "When you borrow money, interest is...", options: ["A fee for using someone else's money", "A bonus prize", "A type of coin", "Free"], answer: 0, explain: "The lender gives up the use of that money, so they charge a fee." },
      { q: "Does a bank pay YOU interest for saving?", options: ["Never", "Yes, a little", "Only on birthdays", "Only if you ask nicely"], answer: 1, explain: "Savings accounts pay interest - a reward for keeping money there." },
      { q: "What does a household credit report track?", options: ["Your favorite snacks", "Every loan and whether it was repaid on time", "Screen time", "Grades"], answer: 1, explain: "It's a small version of what credit bureaus track for adults." },
      { q: "You borrow $12 with $1 flat interest. You owe...", options: ["$12", "$11", "$13", "$24"], answer: 2, explain: "$12 borrowed + $1 interest = $13." },
      { q: "Diego repaid nine loans on time. What did that earn him?", options: ["Nothing", "More trust - a bigger allowance and a debit card", "A lecture", "A new bike"], answer: 1, explain: "A track record earns access to more." },
    ],
  },
  {
    id: "m3",
    slug: "score-explorers",
    num: 3,
    ages: "12-14",
    short: "Score Explorers",
    title: "Understanding Credit Scores & Reports",
    tagline: "Crack the code behind the number",
    color: "#63c79a",
    dark: "#0d4d33",
    button: "#7d5fb2",
    pandaAccessory: "glasses",
    pandaScarf: "#7d5fb2",
    pandaItem: "none",
    objectives: [
      "Explain, in the child's own words, what a credit score is and what it's used for.",
      "Identify the five factors that make up a FICO score and rank them by weight.",
      "Understand why checking your own credit report is different from a 'hard inquiry' that can affect your score.",
    ],
    concepts: [
      { icon: "Gauge", title: "The real vocabulary", text: "Credit score, credit report, credit bureau, hard inquiry, soft inquiry, utilization. Middle schoolers can connect a number to a consequence: a lower score can mean a higher interest rate, which means paying more for the same car." },
      { icon: "Building2", title: "Three bureaus", text: "Equifax, Experian and TransUnion each keep a report, and reports can differ slightly between them. A hard inquiry (applying for new credit) can briefly affect a score; a soft inquiry (checking your own report) does not." },
      { icon: "PieChart", title: "Five factors, five weights", text: "Payment history 35%, amounts owed 30%, length of history 15%, credit mix 10%, new credit 10%. Talk it through as a family - real formulas are proprietary, so this is directional, not arithmetic." },
    ],
    script: [
      { who: "parent", text: "If your credit score were a school report card, what do you think 'payment history' would be graded on?" },
      { who: "child", text: "Whether I turned things in on time?" },
      { who: "parent", text: "Exactly. And it's worth more of your grade than almost anything else - about 35%. What do you think 'amounts owed' means?" },
      { who: "child", text: "How much I still have to pay back?" },
      { who: "parent", text: "Close - it's really about how much of your available credit you're using. Owing a little of a lot is safer than owing a lot of a little." },
    ],
    worksheet: {
      title: "Factor Match-Up",
      intro: "For each factor, write one real-life example you can give.",
      columns: ["Factor", "Weight", "One Real-Life Example You Can Give"],
      rows: [["Payment history", "35%"], ["Amounts owed", "30%"], ["Length of history", "15%"], ["Credit mix", "10%"], ["New credit", "10%"]],
    },
    caseStudy: {
      title: "Reading a Sample Report",
      text: "At 13, Jordan's mother pulled up a sample credit report from myFICO.com's education pages and walked through it line by line: account name, date opened, credit limit, current balance, payment history grid. Jordan noticed the payment-history grid looked like an attendance chart. 'So one red mark just sits there for years?' he asked, correctly identifying that a single late payment can remain visible even after it's paid.",
      lesson: "The habit was the same as the household credit report two years earlier - only the stakes were now real.",
    },
    dos: ["Show a real (sample or your own, redacted) credit report.", "Name the three bureaus and that reports can differ."],
    donts: ["Let the child believe checking their own credit hurts their score.", "Present the score as a single fixed truth rather than a snapshot that changes."],
    quiz: [
      { q: "Which factor weighs the most in a FICO score?", options: ["New credit", "Credit mix", "Payment history (35%)", "Length of history"], answer: 2, explain: "Paying on time is worth more than almost anything else." },
      { q: "Checking your own credit report is a...", options: ["Hard inquiry - it hurts your score", "Soft inquiry - it does not affect your score", "Crime", "Waste of time"], answer: 1, explain: "Soft inquiries never affect your score." },
      { q: "Which are the three credit bureaus?", options: ["Visa, Mastercard, Amex", "Equifax, Experian, TransUnion", "FICO, Vantage, Beacon", "Fannie, Freddie, FHA"], answer: 1, explain: "Each bureau keeps its own report, and they can differ slightly." },
      { q: "'Utilization' means...", options: ["How many cards you own", "How much of your available credit you're using", "How fast you pay", "Your income"], answer: 1, explain: "Owing a little of a lot is safer than owing a lot of a little." },
      { q: "How long can a late payment stay on a report?", options: ["1 month", "1 year", "Up to 7 years", "Forever"], answer: 2, explain: "7 years from the date of the original delinquency." },
    ],
  },
  {
    id: "m4",
    slug: "profile-builders",
    num: 4,
    ages: "15-17",
    short: "Profile Builders",
    title: "Building a Credit Profile Before Adulthood",
    tagline: "Start a real track record, safely",
    color: "#ee7aa6",
    dark: "#7a1f45",
    button: "#1b6fb5",
    pandaAccessory: "cap",
    pandaScarf: "#63c79a",
    pandaItem: "card",
    objectives: [
      "Compare becoming an authorized user vs. opening a secured credit card.",
      "Practice reading a real credit card statement: APR, minimum payment, statement balance, due date.",
      "Set a personal credit-score goal for age 18 and identify two concrete steps toward it.",
    ],
    concepts: [
      { icon: "CreditCard", title: "Two safe on-ramps", text: "Become an authorized user on a parent's well-managed card, or (once eligible) open a secured card backed by a cash deposit the teen controls. Both report to the bureaus; both carry real risk if mismanaged." },
      { icon: "Percent", title: "Utilization, for real", text: "On a real or sample statement, point to the credit limit and the current balance and calculate the percentage together. Under 30%, ideally under 10% - not the older 'up to 50%' advice." },
      { icon: "ShieldCheck", title: "An emergency cushion", text: "Aim for a buffer of a few months of expenses, separate from credit, so a missed paycheck or surprise bill never has to become a missed credit payment." },
    ],
    script: [
      { who: "child", text: "Can I just get my own credit card at 16?" },
      { who: "parent", text: "Not on your own - you're not old enough to sign a credit contract yet. But I can add you as an authorized user on mine, or we can open a secured card in your name with your own savings as the deposit. Which sounds better to you?" },
      { who: "child", text: "What's the difference?" },
      { who: "parent", text: "On my card, my habits show up on your report too - good or bad. On a secured card, it's entirely your own track record from day one, but you put down a deposit, usually a few hundred dollars, that becomes your limit." },
    ],
    worksheet: {
      title: "Statement Detective",
      intro: "Read one real statement together, line by line.",
      columns: ["Statement Item", "Where to Find It", "This Month's Number", "What It Means for You"],
      rows: [["Credit limit"], ["Current balance"], ["Utilization (balance / limit)"], ["Minimum payment due"], ["Due date"], ["APR (interest rate)"]],
    },
    caseStudy: {
      title: "Authorized User vs. Secured Card",
      text: "Sixteen-year-old Priya's parents offered both options. She chose to become an authorized user on her mother's card, which had a 15-year history of on-time payments and low utilization - that history immediately became part of Priya's own credit file. Her older brother, at the same age, had chosen a secured card with a $300 deposit instead, because he wanted a file that was entirely his own.",
      lesson: "Both approaches are legitimate; discussing the tradeoff openly (faster history vs. full independence) is the actual lesson.",
    },
    dos: ["Read one real statement together, line by line.", "Let the teen choose between authorized-user and secured-card paths."],
    donts: ["Add a teen as an authorized user on an account with a history of late payments.", "Open a secured card without discussing the deposit and fees involved."],
    quiz: [
      { q: "Two low-risk ways to start building credit before 18?", options: ["Payday loan or car loan", "Authorized user or secured card", "Buy stocks or crypto", "None exist"], answer: 1, explain: "Both report to the bureaus and both are legitimate." },
      { q: "What utilization target helps a score?", options: ["30-50%", "Under 30%, ideally under 10%", "100%", "It doesn't matter"], answer: 1, explain: "The old 'up to 50%' advice is too high." },
      { q: "On a secured card, what sets your credit limit?", options: ["Your age", "Your grades", "Your cash deposit", "The bank's mood"], answer: 2, explain: "The deposit you put down typically becomes your limit." },
      { q: "As an authorized user, whose habits show on your report?", options: ["Only yours", "The primary cardholder's too", "Nobody's", "Your teacher's"], answer: 1, explain: "Good or bad, the account's history becomes part of your file." },
      { q: "Why build an emergency cushion?", options: ["To buy more stuff", "So a surprise bill never becomes a missed credit payment", "To impress friends", "Banks require it"], answer: 1, explain: "A cushion keeps a bad month from becoming a bad credit report." },
    ],
  },
  {
    id: "m5",
    slug: "independent-credit",
    num: 5,
    ages: "18-24",
    short: "Launch Pad",
    title: "Establishing and Protecting Independent Credit",
    tagline: "Your own credit, your own rules",
    color: "#6dcdf2",
    dark: "#0c4a6e",
    button: "#e5356f",
    pandaAccessory: "crown",
    pandaScarf: "#f7931e",
    pandaItem: "coin",
    objectives: [
      "Pull and review a full credit report from all three bureaus for free.",
      "Set a 12-month credit-building plan with three specific accounts or actions.",
      "Recognize warning signs of predatory lending and debt settlement scams.",
    ],
    concepts: [
      { icon: "Search", title: "First move: pull your report", text: "annualcreditreport.com is the only site required by federal law to provide free reports from all three bureaus. Check it for errors or, more seriously, signs of identity theft." },
      { icon: "ListChecks", title: "A workable starter plan", text: "One on-time-paid credit card kept under 10-30% utilization, one on-time phone or utility bill, and, if applicable, a small installment loan paid consistently. The goal is a mix, not maximum debt." },
      { icon: "AlertTriangle", title: "Know the traps", text: "Payday loans, high-fee 'credit repair' that promises to erase accurate negative information (federal law doesn't allow that), and debt settlement companies that tell you to stop paying creditors. Know these exist before you're targeted." },
    ],
    script: [
      { who: "child", text: "I got a text saying I can raise my credit score 100 points for $300." },
      { who: "parent", text: "That's a red flag. No company can legally remove accurate negative information from your report - only inaccurate items can be disputed and removed, and you can do that yourself for free." },
      { who: "child", text: "So what actually raises a score?" },
      { who: "parent", text: "Time, on-time payments, and low utilization. There's no shortcut, and anyone selling one is selling something else." },
    ],
    worksheet: {
      title: "My 12-Month Credit Plan",
      intro: "Three specific actions, with a check-in date for each.",
      columns: ["12-Month Goal", "Action Step 1", "Action Step 2", "Check-In Date"],
      rows: [["Pull full credit report (all 3 bureaus)"], ["Open/maintain one revolving account under 30% utilization"], ["Build 3-6 month emergency cushion"], ["Reach personal score goal"]],
    },
    caseStudy: {
      title: "The $300 Text Message",
      text: "At 19, Marcus received a text promising a 100-point score increase for a $300 upfront fee. Because his parents had walked him through this module's script two years earlier, he recognized the offer as a scam pattern: no legitimate company can remove accurate negative information, and legitimate credit-repair services are not allowed to demand payment before performing work under the Credit Repair Organizations Act.",
      lesson: "He disputed one genuinely inaccurate collection account himself, for free, directly with the bureau - and it was removed within 30 days.",
    },
    dos: ["Pull a free report from annualcreditreport.com annually.", "Dispute genuine errors directly with the bureau, in writing, for free."],
    donts: ["Pay a company that promises to remove accurate negative information.", "Sign up for a debt settlement plan without understanding it stops direct payments to creditors."],
    quiz: [
      { q: "The only federally authorized free credit report site is...", options: ["freecreditscore.biz", "annualcreditreport.com", "myscore4free.net", "There isn't one"], answer: 1, explain: "It provides free reports from all three bureaus." },
      { q: "A text offers +100 points for $300 upfront. That's...", options: ["A great deal", "A scam red flag", "Normal banking", "Required by law"], answer: 1, explain: "Upfront fees and guaranteed results are classic scam patterns." },
      { q: "Can a company legally remove ACCURATE negative info?", options: ["Yes, for a fee", "No - only inaccurate items can be removed", "Only on weekends", "Yes, if you ask twice"], answer: 1, explain: "And you can dispute inaccurate items yourself, for free." },
      { q: "Debt settlement programs typically...", options: ["Pay your bills for you", "Tell you to stop paying creditors while they negotiate", "Raise your score instantly", "Are free"], answer: 1, explain: "That gap in payments is what gets reported as delinquent." },
      { q: "What actually raises a score?", options: ["Time, on-time payments, low utilization", "Paying a repair company", "Closing every account", "Applying for 10 cards"], answer: 0, explain: "There's no shortcut - anyone selling one is selling something else." },
    ],
  },
];

export const testimonials = [
  { name: "Dana R.", role: "Mom of two, Ohio", text: "My 7-year-old now asks to 'write it on the fridge' whenever she borrows a dollar. The promise jars changed how we talk about money." },
  { name: "Marcus T.", role: "Dad & high-school teacher", text: "The Statement Detective worksheet finally made APR and utilization click for my 16-year-old. We did it in one sitting." },
  { name: "Grandma Lou", role: "Grandparent, Texas", text: "I wish someone had taught me the household credit report idea fifty years ago. Now my grandson keeps one on his own." },
];

export const getModuleBySlug = (slug) => modules.find((m) => m.slug === slug);
